import React, { useMemo } from 'react'
import { CheckboxProps } from './types'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Componente Checkbox
 * Casilla de verificación
 */
const Checkbox: React.FC<CheckboxProps> = ({
  label,
  size = 'md',
  className = '',
  error = false,
  errorMessage,
  disabled = false,
  ...rest
}) => {
  // Clases según tamaño
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'h-4 w-4'
      case 'lg':
        return 'h-5 w-5'
      default:
        return 'h-4 w-4'
    }
  }, [size])

  // Clases para el checkbox
  const checkboxClasses = useMemo(() => {
    const baseClasses = `rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${sizeClasses}`
    
    const stateClasses = disabled
      ? 'bg-gray-100 cursor-not-allowed opacity-50'
      : error
      ? 'border-red-300 focus:ring-red-500'
      : 'cursor-pointer'
    
    return `${baseClasses} ${stateClasses}`
  }, [sizeClasses, disabled, error])

  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          disabled={disabled}
          className={checkboxClasses}
          aria-invalid={error}
          {...rest}
        />
        {label && (
          <label
            className={`text-sm font-medium ${
              disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'
            }`}
            onClick={() => !disabled && rest.onChange && rest.onChange({} as React.ChangeEvent<HTMLInputElement>)}
          >
            {label}
          </label>
        )}
      </div>

      {/* Mensaje de error */}
      {error && errorMessage && (
        <div className="mt-1 flex items-center gap-1 animate-fade-in">
          <ExclamationCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600">{errorMessage}</span>
        </div>
      )}
    </div>
  )
}

export default React.memo(Checkbox)

